import { MAX_FRAME_BYTES } from "../envelope";

/**
 * captureFrame - the shared freeze's capture primitives (D-05, D-07, RS-11).
 *
 * Turns a live `<video>` (the stage's own element, or a temporary one attached to a
 * `MediaStreamTrack`) into a size-bounded JPEG. Guards against the two known Safari failure
 * modes (RESEARCH Pitfall 4): capture waits for a decodable frame (`readyState >= 2`) instead of
 * assuming one is ready, and the source video is never removed from layout or set non-displayed -
 * a temporary capture element is styled near-invisible (opacity 0, 2x2px, fixed) but stays laid
 * out and playing.
 */

export const JPEG_QUALITY = 0.85;
export const MAX_LONG_EDGE = 1920;

export type CapturedFrame = { blob: Blob; width: number; height: number; suspectBlack: boolean };

export class FrameCaptureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FrameCaptureError";
  }
}

const READY_STATE_HAVE_CURRENT_DATA = 2;
const VIDEO_READY_TIMEOUT_MS = 3000;
const BLACK_RETRY_DELAY_MS = 250;
const BLACK_LUMINANCE_THRESHOLD = 8;
const SAMPLE_EDGE = 16;

/** Proportionally scales (w, h) so its longest edge is at most `maxLongEdge`; integers, min 1. */
export function scaledSize(width: number, height: number, maxLongEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge) {
    return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
  }
  const scale = maxLongEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Mean perceptual luminance (alpha ignored) below `threshold` counts as mostly black. */
export function isMostlyBlack(rgba: Uint8ClampedArray, threshold = BLACK_LUMINANCE_THRESHOLD): boolean {
  let sum = 0;
  let count = 0;
  for (let i = 0; i + 2 < rgba.length; i += 4) {
    sum += 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
    count += 1;
  }
  if (count === 0) {
    return true;
  }
  return sum / count < threshold;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Resolves once the video has a decodable frame (readyState >= HAVE_CURRENT_DATA, non-zero intrinsic size). */
function waitForDecodableFrame(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= READY_STATE_HAVE_CURRENT_DATA && video.videoWidth > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onLoadedData = () => {
      if (video.readyState >= READY_STATE_HAVE_CURRENT_DATA && video.videoWidth > 0) {
        clearTimeout(timeoutId);
        video.removeEventListener("loadeddata", onLoadedData);
        resolve();
      }
    };
    const timeoutId = setTimeout(() => {
      video.removeEventListener("loadeddata", onLoadedData);
      reject(new FrameCaptureError("The video has no frame yet"));
    }, VIDEO_READY_TIMEOUT_MS);
    video.addEventListener("loadeddata", onLoadedData);
  });
}

/** Resolves after the next fresh frame - requestVideoFrameCallback when present, else one rAF. */
function waitForFreshFrame(video: HTMLVideoElement): Promise<void> {
  const withRvfc = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (callback: () => void) => number;
  };
  if (typeof withRvfc.requestVideoFrameCallback === "function") {
    return new Promise((resolve) => {
      withRvfc.requestVideoFrameCallback?.(() => resolve());
    });
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function drawFrameToCanvas(video: HTMLVideoElement): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const { width, height } = scaledSize(video.videoWidth, video.videoHeight, MAX_LONG_EDGE);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new FrameCaptureError("Could not get a 2d canvas context");
  }
  // The video's intrinsic, unmirrored frame is the canonical image (D-05) - drawImage never
  // applies the local CSS mirror transform, only the source pixels.
  ctx.drawImage(video, 0, 0, width, height);
  return { canvas, ctx };
}

function sampleIsBlack(canvas: HTMLCanvasElement): boolean {
  const sampleWidth = Math.min(SAMPLE_EDGE, canvas.width);
  const sampleHeight = Math.min(SAMPLE_EDGE, canvas.height);
  if (sampleWidth <= 0 || sampleHeight <= 0) {
    return false;
  }
  const sampleCanvas = document.createElement("canvas");
  sampleCanvas.width = sampleWidth;
  sampleCanvas.height = sampleHeight;
  const sampleCtx = sampleCanvas.getContext("2d");
  if (!sampleCtx) {
    return false;
  }
  sampleCtx.drawImage(canvas, 0, 0, sampleWidth, sampleHeight);
  const { data } = sampleCtx.getImageData(0, 0, sampleWidth, sampleHeight);
  return isMostlyBlack(data);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new FrameCaptureError("Could not encode the captured frame"));
        }
      },
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/**
 * Captures one JPEG frame from an already-rendered `<video>` element (the worker's own stage
 * video, in the fallback path). Waits for a decodable, fresh frame; retries once after 250ms if
 * the sampled frame looks black; never hides or pauses the element.
 */
export async function captureFromVideoElement(video: HTMLVideoElement): Promise<CapturedFrame> {
  await waitForDecodableFrame(video);
  await waitForFreshFrame(video);

  let { canvas } = drawFrameToCanvas(video);
  let suspectBlack = sampleIsBlack(canvas);

  if (suspectBlack) {
    await delay(BLACK_RETRY_DELAY_MS);
    await waitForFreshFrame(video);
    ({ canvas } = drawFrameToCanvas(video));
    suspectBlack = sampleIsBlack(canvas);
  }

  if (suspectBlack) {
    // Flagged for the D-14 real-device check (05-18); never logs image data.
    console.warn("freeze: captured frame looks black");
  }

  const blob = await canvasToBlob(canvas);
  if (blob.size > MAX_FRAME_BYTES) {
    throw new FrameCaptureError("The captured frame is too large");
  }

  return { blob, width: canvas.width, height: canvas.height, suspectBlack };
}

/**
 * Captures one JPEG frame from a `MediaStreamTrack` (the source owner's path): prefers an
 * already-attached, ready `<video>` element, otherwise creates a temporary one - muted,
 * `playsInline`, near-invisible but kept in layout and never non-displayed (Pitfall 4) - captures
 * from it, then removes it.
 */
export async function captureFromTrack(track: MediaStreamTrack, attached?: HTMLVideoElement[]): Promise<CapturedFrame> {
  const readyElement = attached?.find((el) => el.readyState >= READY_STATE_HAVE_CURRENT_DATA);
  if (readyElement) {
    return captureFromVideoElement(readyElement);
  }

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = new MediaStream([track]);
  video.style.position = "fixed";
  video.style.left = "0";
  video.style.top = "0";
  video.style.width = "2px";
  video.style.height = "2px";
  video.style.opacity = "0";
  video.style.pointerEvents = "none";
  document.body.appendChild(video);

  try {
    await video.play().catch(() => {});
    return await captureFromVideoElement(video);
  } finally {
    video.remove();
    video.srcObject = null;
  }
}
